import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import ExportButtons from '../../../components/ExportButtons'
import type { ColumnDefinition, ExportMetadata } from '../../../utils/exportToCsv'
import type { StaatidDrivlinjeRow } from './api'
import { formatAndel, formatDager } from './api'
import { drivlinjeLabel } from './drivlinjer'

type SortCol = 'drivlinje' | 'antall' | 'andel' | 'median_dager' | 'snitt_dager'
type Sort = { col: SortCol; dir: 'asc' | 'desc' } | null

interface Props {
  rows: StaatidDrivlinjeRow[]
  total: number
  loading: boolean
  exportMeta?: Partial<ExportMetadata>
}

function SortIcon({ col, sort }: { col: SortCol; sort: Sort }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  return sort.dir === 'asc'
    ? <ArrowUp className="w-4 h-4 inline ml-1" />
    : <ArrowDown className="w-4 h-4 inline ml-1" />
}

const fmt = (v: number | null) => (v === null ? '-' : String(v))

// Built at render so the headers follow the UI language (#714).
const exportColumns = (t: TFunction<'staatid'>): ColumnDefinition[] => [
  { key: 'drivlinje', label: t('drivlinjer.drivlinje') },
  { key: 'antall', label: t('common.antall'), format: (v: number) => v.toLocaleString('nb-NO') },
  { key: 'andel', label: t('drivlinjer.andel'), format: (v: number) => formatAndel(v) },
  { key: 'median_dager', label: t('common.medianDager'), format: fmt },
  { key: 'snitt_dager', label: t('common.snittDager'), format: fmt },
]

/** The mean over all rows is exact from the per-row means; the median is not
 *  derivable from per-row medians, so the TOTAL row leaves it empty. */
function weightedSnitt(rows: StaatidDrivlinjeRow[], total: number): number | null {
  if (!total) return null
  const sum = rows.reduce((acc, r) => acc + (r.snitt_dager ?? 0) * r.antall, 0)
  return Math.round((sum / total) * 10) / 10
}

export default function DrivlinjerTable({ rows, total, loading, exportMeta }: Props) {
  const { t } = useTranslation('staatid')
  const [sort, setSort] = useState<Sort>(null)

  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="table-skeleton" className="animate-pulse h-48 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  function handleSort(col: SortCol) {
    setSort(prev => (prev?.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' }))
  }

  const labelled = rows.map(r => ({ ...r, drivlinje: drivlinjeLabel(t, r.drivstoff_gruppe) }))
  const sorted = sort
    ? [...labelled].sort((a, b) => {
        const va = a[sort.col]
        const vb = b[sort.col]
        // Missing medians sort last regardless of direction.
        if (va === null) return 1
        if (vb === null) return -1
        const cmp = typeof va === 'string' ? va.localeCompare(String(vb), 'nb') : va - Number(vb)
        return sort.dir === 'asc' ? cmp : -cmp
      })
    : labelled
  const snittTotal = weightedSnitt(rows, total)
  const exportRows = [
    ...sorted,
    { drivlinje: 'TOTAL', antall: total, andel: total ? 1 : 0, median_dager: null, snitt_dager: snittTotal },
  ]

  const th = 'px-4 py-2 font-medium text-gray-600 dark:text-gray-400 cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-dark-border whitespace-nowrap'
  const header = (col: SortCol, label: string, align: 'left' | 'right') => (
    <th className={`${th} text-${align}`} onClick={() => handleSort(col)} data-testid={`drivlinjer-sort-${col}`}>
      {label}<SortIcon col={col} sort={sort} />
    </th>
  )

  return (
    <div data-testid="staatid-drivlinjer-table" className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('drivlinjer.tableTitle')}</span>
        <ExportButtons
          data={exportRows}
          columns={exportColumns(t)}
          metadata={{ title: t('exportTitles.drivlinjer'), ...exportMeta }}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
              {header('drivlinje', t('drivlinjer.drivlinje'), 'left')}
              {header('antall', t('common.antall'), 'right')}
              {header('andel', t('drivlinjer.andel'), 'right')}
              {header('median_dager', t('common.medianDager'), 'right')}
              {header('snitt_dager', t('common.snittDager'), 'right')}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr
                key={row.drivstoff_gruppe}
                data-testid="staatid-drivlinje-rad"
                className={`border-b border-gray-100 dark:border-dark-border ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
              >
                <td className="px-4 py-2">{row.drivlinje}</td>
                <td className="px-4 py-2 text-right tabular-nums">{row.antall.toLocaleString('nb-NO')}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatAndel(row.andel)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.median_dager)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.snitt_dager)}</td>
              </tr>
            ))}
            <tr data-testid="staatid-drivlinje-total" className="font-semibold border-t-2 border-gray-300 dark:border-dark-border">
              <td className="px-4 py-2">TOTAL</td>
              <td className="px-4 py-2 text-right tabular-nums">{total.toLocaleString('nb-NO')}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatAndel(total ? 1 : 0)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatDager(null)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatDager(snittTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
